
import {
  PrimaryButton,
  CQTreeTable,
  DotMenu,
  usePaginationSessionStorage
} from '@laxmimanogna/code-quick-components';

import React, { useContext, useEffect, useMemo, useState } from 'react';
import { 
  Box, 
  Button, 
  HStack, 
  Text, 
  Tooltip, 
  useBoolean,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalOverlay,
  Checkbox,
  CheckboxGroup,
  VStack,
  Input,
  Center,
  useMediaQuery,
  Spacer,
  StackDivider,
  Grid,
  Tabs,
  TabList,
  TabPanel,
  TabPanels
} from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { toTitleCase } from '../../../../utils/common.utils';
import {
  CHART_SUBHEADER,
  ChartStatusBadge,
  BatchStatusIndicator,
  AttachmentCell,
  CompletedDateCell,
  getBatchStatus,
} from './table_config';
import Select from 'react-select';
import { replaceRoute } from '../../../../utils/common.utils';
import AppColors from '../../../../constants/AppColors';
import { format } from 'date-fns';
import { AuditContext } from '../../../../providers/AuditProvider';
import { AuthContext } from '../../../../providers/AuthProvider';
import { createTreeTableProps, paginationProps } from '../../../../utils/cq_tree_table_props';
import { useNavigate } from 'react-router-dom';
import { FONT_FAMILY } from '../../../../constants/Theme';
import { CustomTab } from '../../../../components/CustomTab';
import ROUTES from '../../../../constants/Routes';
import { DotMenuIcon, WarningText } from '../../../../constants/IconData';
import AuditSheetFileViewer from '../../../audit_sheet/components/AuditSheetFileViewer'
import NotesDrawer from '../../../audit_sheet/components/NotesDrawer';
import { EyeAvatarIcon } from '../../../../constants/IconData';
import UrgentConfirmation from '../confirmation_modals/UrgentConfirmation';
import DeleteConfirmation from '../confirmation_modals/DeleteConfirmation';
import { SplitAuditModal } from '../SplitAuditModal';
import AddAdditionalDocsModal from '../AddAdditionalDocsModal';
import AuditModal from '../../../../components/modal/audit_modal/AuditModal';


const columnHelper = createColumnHelper();

const DropdownComponent = ({
  options,
  setSelected,
  selected,
  placeholder = "Select Options",
  isDisabled=false,
  disabledText = "Dropdown disabled",
  isLoading=false
}) => {
  const handleChange = (option) => {
    setSelected(option);
  }

  return (
    <div style={{ flex: 1 }}>
  {isDisabled ? (
    <Tooltip
      placement="bottom"
      hasArrow
      label={disabledText}
    >
      <div style={{ width: "100%" }}>
        <Select
          styles={{
            container: (base) => ({
              ...base,
              width: "100%",
            }),
          }}
          options={options}
          onChange={handleChange}
          isClearable
          placeholder={placeholder}
          isDisabled={isDisabled}
          value={selected}
          isLoading={isLoading}
        />
      </div>
    </Tooltip>
  ) : (
    <Select
      styles={{
        container: (base) => ({
          ...base,
          width: "100%",
        }),
      }}
      options={options}
      onChange={handleChange}
      isClearable
      placeholder={placeholder}
      isDisabled={isDisabled}
      value={selected}
      isLoading={isLoading}
    />
  )}
</div>
  )
}

const UpdateModal = ({
  isOpen,
  onClose,
  onSubmit,
  rowId,
  tabId
}) => {
  const [selectedChartIds, setSelectedChartIds] = useState([]);
  const [value, setValue] = useState("");
  const [search, setSearch] = useState("");
  const [isNotSmallScren] = useMediaQuery("(min-width: 410px)");
  const auditContext = useContext(AuditContext)
  const { 
      allCharts, 
      isAllChartsLoading, 
      getAllCharts, 
      filterParams, 
      getClientSpecialties,
      isFetchingSpecialties,
      isFetchingReviewers,
      getCommonReviewers,
      getReviewers
  } = auditContext;

  const [chartOptions, setChartOptions] = useState([]);
  const [chartIdToObject, setChartIdToObject] = useState({})
  const [specialties, setSpecialties] = useState([])
  const [reviewers, setReviewers] = useState(null);
  const [selectedSpecialty, setSelectedSpecialty] = useState(null);
  const [selectedReviewer, setSelectedReviewer] = useState(null);
  const [selectedTab, setSelectedTab] = useState(tabId);
  const [chartListWarning, setChartListWarning] = useState(null);
  const [dropdownWarning, setDropdownWarning] = useState(null);
  const [isDropdownFiltering, idpState] = useBoolean(false);
  const [auditorList, setAuditorList] = useState([]);
  const [qaList, setQaList] = useState([]);
  const [isReviewersInitialized, iriState] = useBoolean(false);
  const [isSubmitting, isState] = useBoolean(false);
  
  const selectedChartSpecialties = useMemo(() => {
    return Object.fromEntries(selectedChartIds.filter(id => !!chartIdToObject[id].specialty).map(chartId => [chartIdToObject[chartId].specialty.id, chartIdToObject[chartId].specialty]))
  }, [selectedChartIds])

  useEffect(() => {
    setSelectedTab(tabId);
  }, [tabId])

  const selectedChartsAssignedData = useMemo(() => {
    let selChartsData = {
      specialties: [],
      auditors: [],
      qas: []
    }
    selectedChartIds.filter(id => !!chartIdToObject[id].specialty).forEach(chartId => {
      const chartData = chartIdToObject[chartId];
      if (chartData.specialty?.name) {
        selChartsData.specialties.push(chartData.specialty?.name)
      }
      if (chartData.auditor?.full_name) {
        selChartsData.auditors.push(chartData.auditor?.full_name)
      }
      if (chartData.qa?.full_name) {
        selChartsData.qas.push(chartData.qa?.full_name)
      }
    })
    selChartsData.specialties = [...new Set(selChartsData.specialties)]
    selChartsData.auditors = [...new Set(selChartsData.auditors)]
    selChartsData.qas = [...new Set(selChartsData.qas)]

    return selChartsData;
    }, [selectedChartIds])

  const filteredAuditorList = useMemo(() => {
    try {
      idpState.on();
      const selectedSpecIds = Object.keys(selectedChartSpecialties ?? {}).map(Number);
      if (selectedSpecIds.length > 0 && (auditorList ?? []).length > 0) {
        return auditorList.filter(
          user => selectedSpecIds.every(
            spec => (user?.specialties ?? [])
                    .map(spec => spec?.id)
                    .includes(spec)
          )
        )
      }
      return [];
    } finally {
      idpState.off();
    }
  }, [selectedChartSpecialties, auditorList])

  const filteredQaList = useMemo(() => {
    try {
      idpState.on();
      const selectedSpecIds = Object.keys(selectedChartSpecialties ?? {}).map(Number);
      if (selectedSpecIds.length > 0 && (qaList ?? []).length > 0) {
        return qaList.filter(
          user => selectedSpecIds.every(
            spec => (user?.specialties ?? [])
                    .map(spec => spec?.id)
                    .includes(spec)
          )
        )
      }
      return []
      } finally {
        idpState.off();
      }
  }, [selectedChartSpecialties, qaList])

  useEffect(() => {
    if (isOpen && allCharts) {
      getClientSpecialties({client_id: allCharts[rowId]?.uploader?.id}).then(res => setSpecialties(res))
      setChartOptions((allCharts[rowId] ?? []).subRows.map(chart => String(chart.id)))
      setChartIdToObject(prev => {
        let tmp = {};
        (allCharts[rowId] ?? []).subRows.forEach(chart => tmp[String(chart.id)] = {...chart, all_attachments: [
                                chart.file_obj?.name,
                                chart.file_obj?.audited_chart_name,
                                ...(chart.attachments?.map(a => a.file.name) ?? [])
                              ].filter(Boolean)});
        return tmp;
      })
    } 
    if (!isOpen) {
      refreshModalData();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isReviewersInitialized && Object.keys(selectedChartSpecialties ?? {}).length > 0) {
      iriState.on();
      getReviewers({specialty_ids: Object.keys(selectedChartSpecialties), auditor: true}).then(res => {
        setAuditorList(res);
      }
      );
      getReviewers({specialty_ids: Object.keys(selectedChartSpecialties), qa: true}).then(res => {
        setQaList(res);
      }
    )
    }
    
  }, [selectedChartSpecialties])

  const refreshModalData = () => {
    setChartOptions([]);
    setSelectedSpecialty(null);
    setAuditorList([]);
    setQaList([])
    setSelectedReviewer(null);
    setSelectedChartIds([]);
    iriState.off();
  }

  // 🔍 filtered list
  const filteredList = useMemo(() => {
    const searchQuery = search.toLowerCase();
    return chartOptions.filter((item) => {
      return ((chartIdToObject[item].name ?? "").toLowerCase().includes(searchQuery))
       || (chartIdToObject[item].specialty?.name && chartIdToObject[item].specialty.name.toLowerCase().includes(searchQuery))
       || (chartIdToObject[item].all_attachments.some(item => item.toLowerCase().includes(searchQuery)))
       || (chartIdToObject[item].auditor?.full_name && chartIdToObject[item].auditor.full_name.toLowerCase().includes(searchQuery))
       || (chartIdToObject[item].qa?.full_name && chartIdToObject[item].qa.full_name.toLowerCase().includes(searchQuery))
    }
      
    );
  }, [search, chartOptions]);

  // ☑ select all filtered
  const allFilteredSelected =
    filteredList.length > 0 &&
    filteredList.every((item) => selectedChartIds.includes(item));

  const toggleSelectAllFiltered = (checked) => {
    if (checked) {
      const merged = Array.from(new Set([...selectedChartIds, ...filteredList]));
      setSelectedChartIds(merged);
    } else {
      setSelectedChartIds(selectedChartIds.filter((x) => !filteredList.includes(x)));
    }
  };

  // ✅ Default select all when modal opens
  useEffect(() => {
    if (isOpen) {
      idpState.off();
      setSelectedChartIds(chartOptions);
      setSearch("");
      setValue("");
      setSelectedSpecialty(null);
      setSelectedReviewer(null);
    }
  }, [isOpen, chartOptions]);


  const handleSubmit = async () => {
    try {
      isState.on();
      if (onSubmit) {
        await AssignedAuditTabs[selectedTab].submitFn();
      }
    } finally {
      isState.off();
    }
  };

  const commonDropdownValidation = (selected, name) => {
    if (selected) {
      setDropdownWarning("");
    } else {
      setDropdownWarning(`Please select ${name} to update.`)
    }
  }

  const DropdownInfoComponent = ({selectedOption, currentSelectedList = [], type}) => (
    <Box marginTop={2}>
          {selectedOption ? 
          <Text fontSize="sm" color="blue.400" marginTop={2}>
            {type} selected:{' '}
            <Box
              as="span"
              px={2}
              py={0.5}
              bg="blue.100"
              color="blue.700"
              borderRadius="md"
              fontWeight="semibold"
              mx={1}
            >
              {selectedOption.label}
            </Box>
            <br />
            This will be applied to all selected charts
          </Text>
          : (currentSelectedList ?? []).length > 0 ? 
          <Text fontSize="sm" color="blue.400"> {`Current ${type}(s): ${currentSelectedList.join(', ')}`} </Text> 
          : <WarningText message={dropdownWarning} />    
          }
        </Box>
  )

  const AssignedAuditTabs = [
    {
      title: 'Specialty',
      component: (
        <>
          <DropdownComponent 
            options={specialties.map(val => ({value: val.id, label: val.name}))}
            setSelected={setSelectedSpecialty}
            placeholder={"Select Specialty"}
            selected={selectedSpecialty}
            isLoading={isFetchingSpecialties}
            />
            <DropdownInfoComponent
            selectedOption={selectedSpecialty}
            currentSelectedList={selectedChartsAssignedData?.specialties}
            type={"Specialty"}
            />
        </>
      ),
      submitFn: async () => await onSubmit(selectedChartIds.map(Number), {specialty_id: selectedSpecialty.value}),
      handleChartListValidation: () => {
        if (selectedChartIds?.length) {
          setChartListWarning("");
        } else {
          setChartListWarning("No charts selected. Please select 1 or more charts");
        }
      },
      handleDropdownValidation: () => commonDropdownValidation(selectedSpecialty, "specialty")
    },
    {
      title: 'Auditor',
      component: (
        <>
          <DropdownComponent 
            options={(filteredAuditorList || []).map(val => ({value: val.id, label: val.full_name}))}
            setSelected={setSelectedReviewer}
            placeholder={"Select Auditor"}
            selected={selectedReviewer}
            isLoading={isFetchingReviewers}
            isDisabled={isFetchingReviewers || isDropdownFiltering}
            />

          <DropdownInfoComponent
            selectedOption={selectedReviewer}
            currentSelectedList={selectedChartsAssignedData?.auditors}
            type={"Auditor"}
            />
        </>
      ),
      submitFn: async () => await onSubmit(selectedChartIds.map(Number), {auditor_id: selectedReviewer.value}),
      handleChartListValidation: () => {
        if (selectedChartIds?.length) {
          if (selectedChartIds.some(chartId => !chartIdToObject[chartId].specialty)) {
            setChartListWarning("For assigning auditors, please select those charts which have specialty assigned");
          } else {
            setChartListWarning(null);
          }
        } else {
          setChartListWarning("No charts selected. Please select 1 or more charts");
        }   
      },
      handleDropdownValidation: () => commonDropdownValidation(selectedReviewer, "auditor")
    },
    {
      title: 'QA',
      component: (
        <>
          <DropdownComponent 
            options={(filteredQaList || []).map(val => ({value: val.id, label: val.full_name}))}
            setSelected={setSelectedReviewer}
            placeholder={"Select QA"}
            selected={selectedReviewer}
            isLoading={isFetchingReviewers}
            isDisabled={isFetchingReviewers || isDropdownFiltering}
            />

          <DropdownInfoComponent
            selectedOption={selectedReviewer}
            currentSelectedList={selectedChartsAssignedData?.qas}
            type={"QA"}
            />
        </>
      ),
      submitFn: async () => await onSubmit(selectedChartIds.map(Number), {qa_id: selectedReviewer.value}),
      handleChartListValidation: () => {
        if (selectedChartIds?.length) {
          if (selectedChartIds.some(chartId => !chartIdToObject[chartId].auditor)) {
            setChartListWarning("For assigning qa, please select those charts which have both specialty & auditors assigned");
          } else {
            setChartListWarning(null);
          }
        } else {
          setChartListWarning("No charts selected. Please select 1 or more charts");
        }   
      },
      handleDropdownValidation: () => commonDropdownValidation(selectedReviewer, "qa")
    }
  ];

  const [selectedRowId, setSelectedRowId] = useState('');
  const [reqMissingBool, setReqMissingBool] = useBoolean(false);
  // this is for settling the chart list error
  useEffect(() => {
    idpState.off();
    AssignedAuditTabs[selectedTab].handleChartListValidation();
  }, [selectedTab, selectedChartIds])

  useEffect(() => {
    AssignedAuditTabs[selectedTab].handleDropdownValidation();
  }, [selectedTab, selectedSpecialty, selectedReviewer])

  useEffect(() => {
    setSelectedSpecialty(null);
    setSelectedReviewer(null);
  }, [selectedTab])


  const isSubmitDisabled = useMemo(() => {
    return chartListWarning || dropdownWarning;
  }, [chartListWarning, dropdownWarning])



  return (
    <>
      <Modal
        isOpen={isOpen}
        size={isNotSmallScren ? "xl" : "lg"}
        onClose={onClose}
        isCentered
      >
        <ModalOverlay style={{ backgroundColor: AppColors.lightSkyBlue }} />

        <ModalContent p={15} alignItems={"center"}>
          <ModalHeader>
            <Center
              fontSize={"xl"}
              color={AppColors.secondary}
              fontFamily={FONT_FAMILY.baiJamurjee}
              fontWeight="bold"
            >
              {allCharts[rowId]?.name || ""}
            </Center>
          </ModalHeader>

          <ModalCloseButton />

          <ModalBody mb={2} width={"100%"}>
            <Box
            marginBottom={4}>
            <Box
            border="1px solid #E2E8F0"
            borderRadius="md"
            overflow="hidden"
            mb={1}
          >
            {/* 🔍 Search (inside the same box) */}
            <Box p={2} borderBottom="1px solid #E2E8F0">
              <Input
                size="sm"
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Box>

            {/* ☑ Select All */}
            <Box px={2} py={1} borderBottom="1px solid #F1F5F9">
              <Checkbox
                isChecked={allFilteredSelected}
                onChange={(e) => toggleSelectAllFiltered(e.target.checked)}
              >
                Select All
              </Checkbox>
            </Box>

            {/* 📜 Scrollable list */}
            <Box maxH="200px" overflowY="auto" p={2}>
              <CheckboxGroup value={selectedChartIds} onChange={setSelectedChartIds}>
                <VStack align="stretch" spacing={1} width={"100%"}>
                  {filteredList.length > 0 ? (
                    filteredList.map((item, idx) => (
                      <Box
                      as="label"
                      key={idx}
                      px={2}
                      py={1}
                      borderRadius="md"
                      _hover={{ bg: "gray.100" }}
                      cursor="pointer"
                      width="100%"
                      display="flex"
                      alignItems="center"
                      borderBottom={"1px solid"}
                      borderColor="gray.300"
                    >

                      <Grid
                        templateColumns="20% 20% 25% 25% 10%"
                        w="100%"
                        alignItems="center"
                        px={1}
                        py={0}
                        borderRadius="md"
                        _hover={{ bg: "gray.100" }}
                      >

                        {/* 1st column chart id*/}
                        <HStack spacing={2} minW={0}>
                          <Checkbox 
                          value={item}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                          }} />
                          <Text fontSize="sm" noOfLines={1}>
                            {chartIdToObject[item].name}
                          </Text>
                        </HStack>


                        {/* 2nd col: specialty */}
                        <Box
                          px={3}
                          textAlign="left"
                          borderLeft="2px solid"
                          borderColor="gray.300"
                        >
                          <AttachmentCell attachments={[chartIdToObject[item].specialty?.name]} />
                        </Box>

                        {/* 3rd column: auditor */}
                        <Box
                          px={3}
                          textAlign="left"
                          borderLeft="2px solid"
                          borderColor="gray.300"
                          noOfLines={1}
                        >
                          {chartIdToObject[item].auditor && (
                          <AttachmentCell attachments={[chartIdToObject[item].auditor?.full_name]} />
                          )}
                          
                        </Box>

                        {/* 4th Column: qa */}
                        <Box
                          px={3}
                          textAlign="left"
                          borderLeft="2px solid"
                          borderColor="gray.300"
                        >
                          <AttachmentCell attachments={[chartIdToObject[item].qa?.full_name]} />
                        </Box>

                        {/* 5th col: file viewer */}
                        <Box
                        paddingTop={1}
                        display="flex"
                        alignItems="center"        // vertical center
                        justifyContent="flex-end"  // push to right
                        h="100%"                   
                        >
                          <AuditSheetFileViewer
                            auditData={chartIdToObject[item]}
                            icon={<EyeAvatarIcon 
                              style={{ cursor: 'pointer', width: 30, height: 30 }} 
                               onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.1)')}
                                onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
                                />}
                            chart_id={chartIdToObject[item].id}     
                          />
                        </Box>

                      </Grid>
                    </Box>
                    ))
                  ) : (
                    <Text fontSize="sm" color="gray.400">
                      No results found
                    </Text>
                  )}
                </VStack>
              </CheckboxGroup>
            </Box>
          </Box>
          <WarningText message={chartListWarning} />
          <Text fontSize="sm" color="blue.400">{`Specialties selected: ${(selectedChartsAssignedData.specialties ?? []).join(', ') || "N/A"}`}</Text>
          </Box>

            {/* ✍ INPUT */}
        
            <Tabs
                      fontFamily={FONT_FAMILY.baiJamurjee}
                      onChange={setSelectedTab}
                      index={selectedTab}
                    >
                      <TabList borderBottom={'1px solid'}>
                        <HStack w={'100%'} justifyContent={'space-between'}>
                          <HStack spacing={4}>
                            {AssignedAuditTabs.map((aa, index) => {
                              return (
                                <CustomTab
                                  key={`assigned-audit1-tab-${index}`}
                                  pb={2}
                                  color={AppColors.secondary}
                                  fontFamily={FONT_FAMILY.baiJamurjee}
                                >
                                  <Text fontSize="sm" color="gray.800">
                                  {aa.title}
                                  </Text>
                                </CustomTab>
                              );
                            })}
                          </HStack>
                        </HStack>
                      </TabList>
            
                      <TabPanels>
                        {AssignedAuditTabs.map((aa, index) => {
                          return (
                            <TabPanel p={0} key={`assigned-audit1-panel-${index}`}>
                              {aa.component}
                            </TabPanel>
                          );
                        })}
                      </TabPanels>
                    </Tabs>
                    

            {/* 🚀 SUBMIT */}
            <HStack justify="flex-end" mt={4}>
              <Button 
              colorScheme="blue" 
              onClick={handleSubmit}
              isLoading={isSubmitting}
              isDisabled={isSubmitDisabled}>
                Submit
              </Button>
            </HStack>

          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

const NewAuditTable = ({onOpenNotes, PAGE_NAME, TABLE_NAME}) => {
    const auditContext = useContext(AuditContext);
    const authContext = useContext(AuthContext);
    const { 
        allCharts, 
        isAllChartsLoading,
        getAllCharts, 
        filterParams,
        updateChartsV2,
        getAuditUpload
    } = auditContext;

    const {
      getPaginationSession,
      setPaginationSession
    } = usePaginationSessionStorage();

    const [highlightedRowId, setHighlightedRowId] = useState(null);
    const navigate = useNavigate();
    const [ordering, setOrdering] = useState('');
    const tableRef = React.useRef(null);
    const [isOpen, ioState] = useBoolean(false);
    const [modalRowId, setModalRowId] = useState(null);
    const [tabId, setTabId] = useState(0);

    const [selectedRowId, setSelectedRowId] = useState('');
    const [reqMissingBool, setReqMissingBool] = useBoolean(false);
    const [showSplitModal, ssmState] = useBoolean(false);
    const [showAddDocsModal, sadmState] = useBoolean(false);
    const [currentSplitUpload, setCurrentSplitUpload] = useState({});
    const [currentAuditUpload, setCurrentAuditUpload] = useState({});
    const [textArea, setTextArea] = useState('');
    const [textAreaValidation, setTextAreaValidation] = useBoolean(false);

    function handleSplitAudit(row) {
      ssmState.on();
      setCurrentSplitUpload(row);
    }
    
    function handleAddAdditionalDocs(row) {
      sadmState.on();
      setCurrentAuditUpload(row);
    }

    const requestMissingInfo = async () => {
      if (textArea?.length === 0 || textArea === ' ') {
        setTextAreaValidation.on();
      } else {
        await auditContext.requestMissingInfo(selectedRowId, textArea);
        setReqMissingBool.off();
      }
    };

    const renderActionComponent = auditUpload => {
      return (
        <HStack>
          <AuditSheetFileViewer
            auditData={auditUpload}
            icon={<EyeAvatarIcon 
              style={{ cursor: 'pointer'}} 
              onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.1)')}
              onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}/>}
            chart_id={auditUpload.id}     
          />
          <DotMenu
            icon={
              <DotMenuIcon
                style={{ width: '5px', height: '27px', color: '#D8D8D8' }}
              />
            }
            menuDataItem={[
              {
                onClick: () => handleAddAdditionalDocs(auditUpload),
                children: (
                  <Box p="5px" color={'black'}>
                    Add additional documents
                  </Box>
                ),
              },
              {
                isDisabled:
                  !!auditUpload.is_splitted || auditUpload.total_page == 0,
                onClick: () => handleSplitAudit(auditUpload),
                children: (
                  <Box p="5px" color={'black'}>
                    Split Audit (Don't use)
                  </Box>
                ),
              },
              {
                children: <UrgentConfirmation data={auditUpload} />,
              },
              {
                children: (
                  <div
                    onClick={() => {
                      setReqMissingBool.on();
                      setSelectedRowId(auditUpload.id);
                    }}
                    style={{ color: 'black', padding: '5px' }}
                  >
                    Request Missing Info
                  </div>
                ),
              },
              {
                children: (
                  <DeleteConfirmation
                    data={auditUpload}
                    loadList={() => {
                      getAuditUpload.loadAPI(PAGE_NAME, TABLE_NAME);
                    }}
                  />
                ),
              },
            ]}
          />
        </HStack>
      );
    };

    const ClickableCell = ({ onClick, children }) => {
      const handleMouseEnter = (e) => {
        e.currentTarget.style.background = '#F3F4F6';
        e.currentTarget.style.textDecoration = 'underline';
      };

      const handleMouseLeave = (e) => {
        e.currentTarget.style.background = 'transparent';
        e.currentTarget.style.textDecoration = 'none';
      };

      return (
        <div
          onClick={onClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={{
            width: '100%',
            height: '100%',
            padding: '12px 2px',
            cursor: 'pointer',
            transition: 'background 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {children}
        </div>
      );
    };


    useEffect(() => {
      if (auditContext.allCharts.length > 0) {
        const itemId = getAllCharts.restoreNavigationState(PAGE_NAME, TABLE_NAME, true);
        tableRef.current.collapseAll();
        if (itemId) {
          const navData = itemId.split(',').map(Number);

          // for viewing batch 
          if (navData.length === 2) {
            const [batchRow, batchId] = navData;
            if (allCharts?.[batchRow]?.upload_id == batchId) {
              setHighlightedRowId(batchId);
            } 
          } else {
            // for viewing chart 
            const [batchRow, chartRow , batchId, chartId] = navData;
            if (allCharts?.[batchRow]?.upload_id == batchId && allCharts?.[batchRow]?.subRows?.[chartRow].id === chartId) {
              tableRef.current.expandRow(String(batchRow));
              setHighlightedRowId(chartId);
            }
          }             
                            
          setTimeout(() => {
            setHighlightedRowId(null);
          }, 2000);
        }
      }
    }, [auditContext.allCharts]);
    
    useEffect(() => {
        let searching = !!(filterParams.searchText && filterParams.searchText.trim());
        getAllCharts.loadAPI(PAGE_NAME, TABLE_NAME, searching);
    }, [filterParams, getAllCharts.showOrdering]);

    useEffect(() => {
      if (ordering) {
        getAllCharts.setParams({ ordering });
      }
    }, [ordering]);


    const columns = useMemo(() => [
    
        // ── Col 1: Batch / Chart ID ───────────────────────────────────────────────
        // subHeader value is an object → enables custom bg/color AND sortable toggle
        columnHelper.accessor('name', {
          id: 'name',
          header: () => 'Batch',
          subHeader: {
            1: { label: 'File', ...CHART_SUBHEADER, sortable: true },
          },
          cell: info => {
            const isBatch = info.row.depth === 0;
            const chartData = info.row.original;
            const cellValue = info.getValue();
            const isReceived = chartData.status === 'completed';
            const isTruncated = cellValue?.length > 13;
            const truncatedValue = isTruncated
            ? `${cellValue.slice(0, 10)}...`
            : cellValue;
            if(isBatch){
                return (
                    <Tooltip
                    hasArrow
                    backgroundColor={!isReceived ? AppColors.secondary : undefined}
                    placement="bottom"
                    label={
                        isReceived
                        ? (isTruncated ? cellValue : '') // show only if truncated
                        : (
                            <>
                                Batch pending review.
                                {isTruncated && (
                                <>
                                    <br />
                                    {cellValue}
                                </>
                                )}
                            </>
                            )
                    }
                    >
                    <Text
                        whiteSpace="nowrap"
                        _hover={isReceived ? { textDecoration: 'underline' } : {}}
                        cursor={'pointer'}
                        fontWeight={600}
                        color="blue.800"
                        fontSize="14px"
                        textAlign="start"
                    >
                        {truncatedValue}
                    </Text>
                    </Tooltip>
                );
            }else{
              return <Text fontWeight={500} textAlign="start" color="blue.700" fontSize="13px">{info.getValue()}</Text>;
            }
          },
        }),

        // ── Col 2: Uploader Account ──────────────────────
        columnHelper.accessor('uploader', {
          id: 'account',
          header: () => 'Account',
          subHeader: {
            // 1: { label: 'Attachments', ...CHART_SUBHEADER },
            // sortable omitted → no sort icon on this sub-header column
          },
          cell: info => {
            const isBatch = info.row.depth === 0;
            if (isBatch) {
              return <Text textAlign="center" fontSize="13px">{info.getValue().name}</Text>
            }
          },
        }),
    
        // ── Col 2: Upload Date (batch) / Attachments (chart) ──────────────────────
        columnHelper.accessor('upload_date', {
          id: 'upload_date',
          header: () => 'Upload Date',
          subHeader: {
            1: { label: 'Attachments', ...CHART_SUBHEADER },
            // sortable omitted → no sort icon on this sub-header column
          },
          cell: info => {
            const isBatch = info.row.depth === 0;
            const attachmentNames = [
              info.row.original.file_obj?.name,
              info.row.original.file_obj?.audited_chart_name,
              ...(info.row.original.attachments?.map(a => a.file.name) ?? [])
            ].filter(Boolean);
            if (isBatch) {
              const val = info.getValue();
              return val
                ? <Text textAlign="center" fontSize="14px" whiteSpace={"nowrap"}>{format(new Date(val), 'MM/dd/yyyy')}</Text>
                : <Text color="gray.400" fontSize="14px">N/A</Text>;
            }
            return <AttachmentCell attachments={attachmentNames} />;
          },
        }),
       
    
        // ── Col 3: Status ─────────────────────────────────────────────────────────
        columnHelper.accessor('status', {
          id: 'status',
          header: () => 'Status',
          subHeader: {
            1: { label: 'Status', ...CHART_SUBHEADER, sortable: true },
          },
          cell: info => {
            const isBatch = info.row.depth === 0;
            if (!isBatch) {
            }
            
            return isBatch
              ? <BatchStatusIndicator subRows={info.row.original.subRows} />
              : <ChartStatusBadge status={info.getValue()} />;
          },
        }),
    
        // ── Col 4: Specialty (chart) ─────────────────────
        columnHelper.accessor(row => row, {
          id: 'specialties',
          header: () => 'Specialties',
          subHeader: {
            // Different label at depth 1 — "No. of Charts" becomes "Specialty"
            1: { label: 'Specialty', ...CHART_SUBHEADER, sortable: true },
          },
          // sortValue tells CQTreeTable how to extract a primitive for sub-header sorting
          // accessorFn returns the whole row object which is not directly sortable
          sortValue: row => row.specialty?.name ?? '',
          cell: info => {
            const row     = info.getValue();
            const isBatch = info.row.depth === 0;
            if (isBatch) {
              
              return (
               <ClickableCell 
               onClick={() => {
                  setTabId(0);
                  ioState.on();
                  setModalRowId(info.row.index);
                }}
                >
                  <AttachmentCell
                   attachments={[...new Set((row.subRows || []).map(chart => chart.specialty?.name).filter(val => !!val))]} defaultMsg={"Assign"}/>
                </ClickableCell>     
              )
            }
            if (!row.specialty)
              return <Text color="gray.400" fontSize="13px" textAlign="center">N/A</Text>;
            return row.specialty?.name
              ? <Text textAlign="center" fontSize="13px">{row.specialty.name}</Text>
              : <Text color="gray.400" fontSize="13px" textAlign="center">N/A</Text>;
          },
        }),

        // ── Col 5: Audtiors ─────────────────────
        columnHelper.accessor(row => row, {
          id: 'auditors',
          header: () => 'Auditors',
          subHeader: {
            // Different label at depth 1 — "No. of Charts" becomes "Specialty"
            1: { label: 'Auditor', ...CHART_SUBHEADER, sortable: true },
          },
          // sortValue tells CQTreeTable how to extract a primitive for sub-header sorting
          // accessorFn returns the whole row object which is not directly sortable
          sortValue: row => row.auditor?.full_name ?? '',
          cell: info => {
            const row     = info.getValue();
            const isBatch = info.row.depth === 0;
            if (isBatch) {
              
              return (
                  <ClickableCell
                  onClick={() => {
                  setTabId(1);
                  ioState.on()
                  setModalRowId(info.row.index);
                }}>
                  <AttachmentCell 
                  attachments={[...new Set((row.subRows || []).map(chart => chart.auditor?.full_name).filter(val => !!val))]}
                  defaultMsg={"Assign"} />
                </ClickableCell>
              )
            }

            if (!row.auditor)
              return <Text color="gray.400" fontSize="13px" textAlign="center">N/A</Text>;
            return row.auditor?.full_name
              ? <Text textAlign="center" fontSize="13px">{row.auditor.full_name}</Text>
              : <Text color="gray.400" fontSize="13px" textAlign="center">N/A</Text>;
          },
        }),


         // ── Col 6: QA ─────────────────────────────────────────────────
        columnHelper.accessor(row => row, {
          id: 'qas',
          header: () => 'QA',
          subHeader: {
            // Different label at depth 1 — "No. of Charts" becomes "Specialty"
            1: { label: 'QA', ...CHART_SUBHEADER, sortable: true },
          },
          // sortValue tells CQTreeTable how to extract a primitive for sub-header sorting
          // accessorFn returns the whole row object which is not directly sortable
          sortValue: row => row.qa?.full_name ?? '',
          cell: info => {
            const row     = info.getValue();
            const isBatch = info.row.depth === 0;
            if (isBatch) {
              
              return (
                  <ClickableCell
                    onClick={() => {
                    setTabId(2);
                    ioState.on()
                    setModalRowId(info.row.index);
                  }}>
                  <AttachmentCell 
                  attachments={[...new Set((row.subRows || []).map(chart => chart.qa?.full_name).filter(val => !!val))]} 
                  defaultMsg={"Assign"}/>
                </ClickableCell>
              )
          
            }
            if (!row.qa)
              return <Text color="gray.400" fontSize="13px" textAlign="center">N/A</Text>;
            return row.qa?.full_name
              ? <Text textAlign="center" fontSize="13px">{row.qa.full_name}</Text>
              : <Text color="gray.400" fontSize="13px" textAlign="center">N/A</Text>;
          },
        }),
    
    
        // ── Col 7: Providers ────────────────────────────────────────────────────
        columnHelper.accessor(row => row, {
          id: 'providers',
          header: () => 'Providers',
          enableSorting: false,
          subHeader: {
            1: { label: 'Providers', ...CHART_SUBHEADER },
          },
          cell: info => {
            const rowVal = info.getValue();
            const isBatch = info.row.depth === 0;
            if (isBatch) {
              return <AttachmentCell attachments={rowVal.batch_providers} />
            }
            return <AttachmentCell attachments={(rowVal.providers ?? []).map(chart_prov => chart_prov.name)} />
          }
        }),
    
        // ── Col 8: Action — no sub-header label needed ────────────────────────────
        columnHelper.accessor(row => row.upload_id, {
          id: 'action',
          header: () => '',
          enableSorting: false,
          // subHeader intentionally omitted → blank cell in sub-header row
          cell: info => {
            const cellValue = info.getValue();
            const isBatch = info.row.depth === 0;
            const status = info.row.original.status;
            const batchId = info.getValue();
            
            return (
              <HStack justifyContent="start" spacing={2}>
                <Tooltip
                  placement="top"
                  hasArrow
                >
                  <Box>
                    <PrimaryButton
                      bg={AppColors.secondary}
                      fontSize="14px"
                      fontWeight="normal"
                      size="sm"
                      borderRadius="3xl"
                      px={5}
                      _hover={{
                        transition: 'all .1s ease',
                        bgColor: AppColors.white,
                        color: AppColors.secondary,
                        outline: `2px solid ${AppColors.secondary}`
                      }}
                      onClick={() => {    
                        const paginationSession = getPaginationSession(PAGE_NAME, TABLE_NAME);                        
                        setPaginationSession(PAGE_NAME, TABLE_NAME, {...paginationSession, 
                          ...('searchText' in filterParams && {search: filterParams.searchText}),
                          ...(getAllCharts.showOrdering && {ordering: getAllCharts.showOrdering})})    
                        if (isBatch) {
                          getAllCharts.saveNavigationState(
                            PAGE_NAME,
                            TABLE_NAME,
                            [info.row.index, info.row.original.upload_id]
                          );
                          navigate(
                            ROUTES.VIEW_BATCH_AUDIT_SHEET.replace(':batchId', cellValue)
                          );
                        } else {
                          getAllCharts.saveNavigationState(
                            PAGE_NAME,
                            TABLE_NAME,
                            [
                              info.row.getParentRow().index, 
                              info.row.index, 
                              info.row.getParentRow().original.upload_id, 
                              info.row.original.id
                            ]
                          );
                          const routeFormat = { uploadId: info.row.original.id };
                          const route = ROUTES.VIEW_AUDIT_SHEET;
                          navigate(replaceRoute(route, routeFormat));
                        }
                      }}
                    >
                      {isBatch ? 'View Batch' : 'View File'}
                    </PrimaryButton>
                  </Box>
                </Tooltip>
    
                {!isBatch ? (
                  // <AuditSheetFileViewer
                  //   auditData={info.row.original}
                  //   icon={<EyeAvatarIcon 
                  //     style={{ cursor: 'pointer'}} 
                  //     onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.1)')}
                  //     onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}/>}
                  //   chart_id={info.row.original.id}     
                  // />
                  renderActionComponent(info.row.original)
                ) : (
                <NotesDrawer
                tabType={"ALL"}
                batchId={batchId}
                onOpenNotes={obj => onOpenNotes(obj, batchId, "ALL")}
                style={{
                    width: 35,
                    height: 35,
                    cursor: 'pointer',
                    display: 'inline-block',
                    transition: "0.3s",
                    filter: "invert(1)"
                  }}
                  
                  onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.1)')}
                  onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
                /> 
                )}
              </HStack>
            );
          }
        }),
    
      ], [isAllChartsLoading]);

    function handlePreviousPage() {
        getAllCharts.loadPrevious(PAGE_NAME, TABLE_NAME);
    }

    function handleNextPage() {
        getAllCharts.loadNext(PAGE_NAME, TABLE_NAME);
    }

    function handleSizeChange(size) {
        getAllCharts.loadSize(PAGE_NAME, TABLE_NAME, size );
    }

    function handlePageChangeByNumber(pageNumber) {
        getAllCharts.loadPageByNumber(PAGE_NAME, TABLE_NAME, pageNumber);
    }
    

    return (
        <Box>
            <CQTreeTable
                ref={tableRef}
                data={allCharts}
                columns={columns}
                getSubRows={(row) => row.subRows}
                manualPagination={true}
                manualSortBy={false}
                isLoading={isAllChartsLoading}
                {...createTreeTableProps(highlightedRowId)}
                {...paginationProps(getAllCharts)}
                onAsc={header => {
                setOrdering(header);
                }}
                onDesc={header => {
                setOrdering(`-${header}`);
                }}
                onPreviousPage={handlePreviousPage}
                onNextPage={handleNextPage}
                onSizeChange={handleSizeChange}
                onPageChangeByNumber={handlePageChangeByNumber}
            />
            <UpdateModal
              isOpen={isOpen}
              onClose={() => ioState.off()}
              onSubmit={async (selectedChartIds, updateData, batchId) => {
                let payload = {...updateData}
                if (batchId) {
                  payload["batch_id"] = batchId;
                } else {
                  payload["chart_ids"] = selectedChartIds;
                }
                const response = await updateChartsV2(payload, modalRowId);
                if (!response.error) {
                  ioState.off();
                }
              }}
              rowId={modalRowId}
              tabId={tabId}
            />
            <SplitAuditModal
              isOpen={showSplitModal}
              onClose={ssmState.off}
              currentUpload={currentSplitUpload}
            />

            <AddAdditionalDocsModal
              isOpen={showAddDocsModal}
              onClose={sadmState.off}
              auditUpload={currentAuditUpload}
              onUploadComplete={() => {
                sadmState.off();
                getAuditUpload.loadAPI(PAGE_NAME, TABLE_NAME);
              }}
            />
            <AuditModal
                isOpen={reqMissingBool}
                onClose={() => setReqMissingBool.off()}
                title={'Audit Hold - Information Request'}
                text={
                  'This will change the audit status to “on hold” until the missing information is received.'
                }
                keyText={'Note : '}
                placeholder={'Chart 12345 is missing information...'}
                height={159}
                width={772}
                submitButton={{
                  children: 'Send',
                  isLoading: auditContext.isReqMissing,
                  loadingText: 'Sending',
                  spinnerPlacement: 'end',
                  disabled: !textArea,
                  onClick: () => {
                    requestMissingInfo();
                  },
                }}
                cancelButton={{
                  children: 'Cancel',
                  onClick: () => {
                    setReqMissingBool.off();
                  },
                }}
                onChange={e => {
                  setTextArea(e.target.value);
                }}
                setAlert={textAreaValidation}
              />
        </Box>
    )
}

export default NewAuditTable;